import {
    getIsRateDocumentsExpired,
    getIsRateDocumentsTerminate,
    getRateTotalDocuments,
} from 'components/RatesPopup/helpers';
import {
    SETTINGS_PAGE_INTEGRATION_RATES,
    SETTINGS_PAGE_TRIAL_RATES,
    SETTINGS_PAGE_WEB_RATES,
    SETTINGS_PAGE_WEB_TRIAL_RATES,
} from 'components/companyCard/components/CompanyRatesSettings/constants';
import { getActiveEmployees, getIsMaxActiveEmployees } from 'lib/helpers';
import moment from 'moment';
import { createSelector } from 'reselect';
import {
    ACCOUNT_RATE_TITLE_MAP,
    ALL_MAXIMAL_RATES,
    checkIsUnlimitedRate,
    getPlannedRates,
    getRateDocumentsLeft,
} from 'services/billing';
import { AccountRate, AccountType, RateStatus } from 'services/enums';
import { t } from 'ttag';

import { BillingAcount, CompanyRate } from '../types/billing';
import { StoreState } from '../types/store';

import {
    getActiveEmployeesSelector,
    getBillingAccounts,
    getCompanyActiveRates,
    getCompanyConfigSettingsMaxArchiveDocumentsCount,
    getCompanyConfigSettingsMaxDocumentsForView,
    getCompanyRates,
    getCurrentCompanyMaxEmployees,
    getMostCheapPaidWebRate,
    getMostExpensiveWebRate,
    getPricePerUser,
} from './app.selectors';

import { TRIAL_WEB_RATE_MAX_EMPLOYEES } from './constants';

const getSlice = (state: StoreState) => state.companyCard;

export const getCompanyCardRoleId = (state: StoreState) =>
    getSlice(state).roleId;

export const getCurrentCompanyRole = (state: StoreState) =>
    getSlice(state).currentCompanyRole;

export const getCompany = (state: StoreState) => getSlice(state).company;

export const getCompanyCardRoles = (state: StoreState) =>
    getSlice(state).company.roles;

export const getCompanyCardActiveEmployeesSelector = createSelector(
    getCompanyCardRoles,
    getActiveEmployees,
);

export const getCompanyCardActiveEmployeesCountSelector = (state: StoreState) =>
    getCompanyCardActiveEmployeesSelector(state)?.length;

export const getIsCompanyCardMaxActiveEmployeesSelector = createSelector(
    getCompanyCardActiveEmployeesCountSelector,
    getCurrentCompanyMaxEmployees,
    getIsMaxActiveEmployees,
);

export const getPlannedRatesForSettings = createSelector(
    getCompanyRates,
    getPlannedRates,
);

export const getWebRatesForSettings = createSelector(
    getCompanyActiveRates,
    (activeRates: CompanyRate[]) => {
        const allActiveWebRates = activeRates.filter((rate) =>
            SETTINGS_PAGE_WEB_RATES.has(rate.rate),
        );
        return allActiveWebRates.reduce<{
            allActivePaidWebRates: CompanyRate[];
            freeRate: CompanyRate[];
        }>(
            (result, currentRate) => {
                if (currentRate.rate === AccountRate.FREE) {
                    result.freeRate.push(currentRate);
                } else {
                    result.allActivePaidWebRates.push(currentRate);
                }
                return result;
            },
            { allActivePaidWebRates: [], freeRate: [] },
        );
    },
);

export const getFormattedWebRatesForSettings = createSelector(
    getWebRatesForSettings,
    getBillingAccounts,
    getCompanyConfigSettingsMaxDocumentsForView,
    getCompanyConfigSettingsMaxArchiveDocumentsCount,
    getActiveEmployeesSelector,
    getCurrentCompanyMaxEmployees,
    getMostCheapPaidWebRate,
    getPricePerUser,
    (
        { allActivePaidWebRates, freeRate },
        billingAccounts,
        maxDocumentsCountForView,
        maxArchiveDocumentsCount,
        activeEmployees,
        maxEmployeesCount,
        mostCheapPaidRate,
        pricePerUser,
    ) => {
        const webRates = allActivePaidWebRates.length
            ? allActivePaidWebRates
            : freeRate;

        const currentFreeFate = freeRate[0];
        const documentsLeftFromFreeRate = getRateDocumentsLeft(
            billingAccounts,
            currentFreeFate,
            RateStatus.ACTIVE,
        );
        const maxDocumentsCountForSentFromFreeRate = getRateTotalDocuments(
            currentFreeFate,
            billingAccounts,
            RateStatus.ACTIVE,
        );

        return webRates.map((rate) => {
            const { limits } = rate.config;

            const isMostCheapPaidRate = mostCheapPaidRate.rate === rate.rate;
            const isUnlimitedRate = checkIsUnlimitedRate(rate);
            const isNeedMergeDocumentsFromFreeRate =
                isMostCheapPaidRate && !isUnlimitedRate;

            const documentsLeft = getRateDocumentsLeft(
                billingAccounts,
                rate,
                RateStatus.ACTIVE,
                isNeedMergeDocumentsFromFreeRate
                    ? documentsLeftFromFreeRate
                    : 0,
            );
            const maxDocumentsCountForSent = getRateTotalDocuments(
                rate,
                billingAccounts,
                RateStatus.ACTIVE,
                isNeedMergeDocumentsFromFreeRate
                    ? maxDocumentsCountForSentFromFreeRate
                    : 0,
            );

            const documentsSent =
                (maxDocumentsCountForSent || 0) - (+documentsLeft || 0);
            const infinity = '∞';
            const sendDocumentsMaxValue =
                AccountRate.FREE === rate.rate
                    ? maxDocumentsCountForSent
                    : infinity;

            const isMaxSentDocuments =
                getIsRateDocumentsTerminate(rate, billingAccounts) ||
                getIsRateDocumentsExpired(rate, billingAccounts);
            const documentsCountForViewLimit =
                (limits?.max_documents_count || maxDocumentsCountForView) +
                (maxArchiveDocumentsCount || 0);
            const isMaxEmployeesCount = getIsMaxActiveEmployees(
                activeEmployees?.length,
                maxEmployeesCount,
            );

            return {
                type: rate.rate,
                name: ACCOUNT_RATE_TITLE_MAP[rate.rate as AccountRate.START],
                endDate: moment(rate.endDate).format('DD.MM.YYYY'),
                sentDocuments:
                    documentsLeft === 'unlimited'
                        ? infinity
                        : `${documentsSent}/${
                              maxDocumentsCountForSent || sendDocumentsMaxValue
                          }`,
                maxDocumentsCountForSent,
                documentsCountForViewLimit: !ALL_MAXIMAL_RATES.includes(
                    rate.rate,
                )
                    ? documentsCountForViewLimit
                    : null,
                employeesCount: `${activeEmployees?.length || 0}/${
                    maxEmployeesCount || infinity
                }`,
                isMaxSentDocuments,
                isMaxEmployeesCount,
                config: rate.config,
                pricePerUser: pricePerUser,
            };
        });
    },
);

export const getFormattedPlannedWebRatesForSettings = createSelector(
    getPlannedRatesForSettings,
    getBillingAccounts,
    getCompanyConfigSettingsMaxDocumentsForView,
    getCurrentCompanyMaxEmployees,
    (
        plannedRates,
        billingAccounts,
        maxDocumentsCountForView,
        maxEmployeesCount,
    ) => {
        return plannedRates
            .filter((rate) => SETTINGS_PAGE_WEB_RATES.has(rate.rate))
            .map((rate) => {
                const { limits } = rate.config;
                const documentsLeft = getRateDocumentsLeft(
                    billingAccounts,
                    rate,
                    RateStatus.NEW,
                );
                const maxDocumentsCountForSent = getRateTotalDocuments(
                    rate,
                    billingAccounts,
                    RateStatus.NEW,
                );

                const infinity = '∞';

                const documentsCountForViewLimit =
                    limits?.max_documents_count || maxDocumentsCountForView;

                return {
                    type: rate.rate,
                    name:
                        ACCOUNT_RATE_TITLE_MAP[rate.rate as AccountRate.START],
                    startDate: moment(rate.startDate).format('DD.MM.YYYY'),
                    sentDocuments:
                        documentsLeft === 'unlimited'
                            ? infinity
                            : `0/${
                                  maxDocumentsCountForSent ||
                                  limits?.max_documents_count ||
                                  infinity
                              }`,
                    documentsCountForView:
                        documentsCountForViewLimit &&
                        rate.rate === AccountRate.FREE
                            ? `0/${documentsCountForViewLimit}`
                            : infinity,
                    employeesCount: `0/${maxEmployeesCount || infinity}`,
                };
            });
    },
);

export const getIntegrationRatesForSettings = createSelector(
    getCompanyActiveRates,
    (activeRates: CompanyRate[]) => {
        return activeRates.filter((rate) =>
            SETTINGS_PAGE_INTEGRATION_RATES.has(rate.rate),
        );
    },
);

export const getIntegrationBillingsWithoutDocs = createSelector(
    getIntegrationRatesForSettings,
    getBillingAccounts,
    (integrationRates, billingAccounts: BillingAcount[]) => {
        if (integrationRates.length) {
            return [];
        }

        const integrationBillings = billingAccounts.filter(
            (billingAccount) =>
                billingAccount.rate === AccountRate.INTEGRATION &&
                (billingAccount.status === RateStatus.ACTIVE ||
                    billingAccount.status === null),
        );

        const bonusAccountBillings = integrationBillings.filter(
            ({ type }) =>
                type === AccountType.CLIENT_BONUS ||
                type === AccountType.CLIENT_BONUS_CUSTOM,
        );
        const bonusAccount = bonusAccountBillings.reduce(
            (result, { unitsLeft }) => {
                result += unitsLeft;
                return result;
            },
            0,
        );

        const mainAccountBillings = integrationBillings.filter(
            ({ type }) => type === AccountType.CLIENT_DEBIT,
        );
        const mainAccount = mainAccountBillings.reduce(
            (result, { type, unitsLeft }) => {
                if (type === AccountType.CLIENT_DEBIT) {
                    result += unitsLeft;
                }
                return result;
            },
            0,
        );

        const isMaxMainDocumentsCount = getIsRateDocumentsTerminate(
            { rate: AccountRate.INTEGRATION },
            mainAccountBillings,
        );
        const isMaxBonusDocumentsCount = getIsRateDocumentsTerminate(
            { rate: AccountRate.INTEGRATION },
            bonusAccountBillings,
        );

        return [
            {
                type: AccountRate.FILL_INTEGRATION,
                name: t`Документи до інтеграції`,
                bonusAccount: bonusAccount.toString() || '',
                mainAccount: mainAccount.toString() || '',
                isMaxMainDocumentsCount,
                isMaxBonusDocumentsCount,
            },
        ];
    },
);

export const getFormattedIntegrationRatesForSettings = createSelector(
    getIntegrationRatesForSettings,
    getBillingAccounts,
    getMostExpensiveWebRate,
    (
        integrationRates,
        allBillingAccounts: BillingAcount[],
        mostExpensiveWebRate,
    ) => {
        return integrationRates.map((rate) => {
            const integrationBillings = allBillingAccounts.filter(
                (billingAccount) =>
                    billingAccount.rate === rate.rate &&
                    (billingAccount.status === RateStatus.ACTIVE ||
                        billingAccount.status === null),
            );
            const bonusAccountBillings = integrationBillings.filter(
                ({ type }) =>
                    type === AccountType.CLIENT_BONUS ||
                    type === AccountType.CLIENT_BONUS_CUSTOM,
            );
            const bonusAccount = bonusAccountBillings.reduce(
                (result, { unitsLeft }) => {
                    result += unitsLeft;
                    return result;
                },
                0,
            );

            const mainAccountBillings = integrationBillings.filter(
                ({ type }) => type === AccountType.CLIENT_DEBIT,
            );
            const mainAccount = mainAccountBillings.reduce(
                (result, { type, unitsLeft }) => {
                    if (type === AccountType.CLIENT_DEBIT) {
                        result += unitsLeft;
                    }
                    return result;
                },
                0,
            );

            const isMaxMainDocumentsCount =
                getIsRateDocumentsTerminate(rate, mainAccountBillings) ||
                getIsRateDocumentsExpired(rate, mainAccountBillings);
            const isMaxBonusDocumentsCount =
                getIsRateDocumentsTerminate(rate, bonusAccountBillings) ||
                getIsRateDocumentsExpired(rate, bonusAccountBillings);

            return {
                type: rate.rate,
                name: ACCOUNT_RATE_TITLE_MAP[rate.rate as AccountRate.START],
                endDate: moment(rate.endDate).format('DD.MM.YYYY'),
                bonusAccount: bonusAccount.toString() || '',
                mainAccount: mainAccount.toString() || '',
                isMaxMainDocumentsCount,
                isMaxBonusDocumentsCount,
                config: rate.config,
                mostExpensiveWebRate,
            };
        });
    },
);

export const getFormattedPlannedIntegrationRatesForSettings = createSelector(
    getPlannedRatesForSettings,
    getBillingAccounts,
    (plannedRates, billingAccounts: BillingAcount[]) => {
        return plannedRates
            .filter((rate) => SETTINGS_PAGE_INTEGRATION_RATES.has(rate.rate))
            .map((rate) => {
                const integrationRate = billingAccounts.filter(
                    (billingAccount) => billingAccount.rate === rate.rate,
                );
                const bonusAccount = integrationRate.reduce(
                    (result, { type, unitsLeft }) => {
                        if (
                            type === AccountType.CLIENT_BONUS ||
                            type === AccountType.CLIENT_BONUS_CUSTOM
                        ) {
                            result += unitsLeft;
                        }
                        return result;
                    },
                    0,
                );
                const mainAccount = integrationRate.reduce(
                    (result, { type, unitsLeft }) => {
                        if (type === AccountType.CLIENT_DEBIT) {
                            result += unitsLeft;
                        }
                        return result;
                    },
                    0,
                );

                return {
                    type: rate.rate,
                    name:
                        ACCOUNT_RATE_TITLE_MAP[rate.rate as AccountRate.START],
                    startDate: moment(rate.startDate).format('DD.MM.YYYY'),
                    bonusAccount: bonusAccount.toString() || '',
                    mainAccount: mainAccount.toString() || '',
                };
            });
    },
);

export const getTrialRatesForSettings = createSelector(
    getCompanyActiveRates,
    (activeRates: CompanyRate[]) => {
        return activeRates.filter((rate) =>
            SETTINGS_PAGE_TRIAL_RATES.has(rate.rate),
        );
    },
);

export const getFormattedTrialRatesForSettings = createSelector(
    getTrialRatesForSettings,
    getBillingAccounts,
    getActiveEmployeesSelector,
    getMostExpensiveWebRate,
    (
        trialRates,
        billingAccounts: BillingAcount[],
        activeEmployees,
        mostExpensiveWebRate,
    ) => {
        return trialRates.map((rate) => {
            const isWebTrial = SETTINGS_PAGE_WEB_TRIAL_RATES.has(rate.rate);

            const totalDaysCount = moment(rate.endDate).diff(
                moment(rate.startDate),
                'days',
            );
            const daysLeft = moment(rate.endDate).diff(moment(), 'days');
            const daysSpentPercent =
                ((totalDaysCount - daysLeft) * 100) / totalDaysCount;

            const bonusAccount = billingAccounts.reduce(
                (result, { rate: rateName, type, units, unitsLeft }) => {
                    const isBonusType =
                        type === AccountType.CLIENT_BONUS ||
                        type === AccountType.CLIENT_BONUS_CUSTOM ||
                        type === AccountType.CLIENT_RATE;
                    const isIntegrationTrialRateType =
                        rateName === AccountRate.INTEGRATION ||
                        rateName === AccountRate.INTEGRATION_TRIAL;

                    if (isBonusType && isIntegrationTrialRateType) {
                        result.unitsLeft += unitsLeft;
                        result.units += units;
                    }

                    return result;
                },
                { units: 0, unitsLeft: 0 },
            );

            const isMaxEmployeesCount = getIsMaxActiveEmployees(
                activeEmployees?.length,
                TRIAL_WEB_RATE_MAX_EMPLOYEES,
            );
            const isBonusDocumentsTerminate =
                bonusAccount.unitsLeft < (bonusAccount.units * 15) / 100;

            return {
                type: rate.rate,
                name: ACCOUNT_RATE_TITLE_MAP[rate.rate as AccountRate.START],
                endDate: moment(rate.endDate).format('DD.MM.YYYY'),
                daysLeft: `${daysLeft}/${totalDaysCount}`,
                daysSpentPercent: Number(daysSpentPercent.toFixed()),
                isWebTrial,
                employeesCount: `${activeEmployees.length}/${TRIAL_WEB_RATE_MAX_EMPLOYEES}`,
                bonusAccount: bonusAccount.unitsLeft.toString() || '',
                isMaxEmployeesCount,
                isBonusDocumentsTerminate,
                config: rate.config,
                mostExpensiveWebRate,
            };
        });
    },
);

export const getFormattedPlannedTrialRatesForSettings = createSelector(
    getPlannedRatesForSettings,
    getBillingAccounts,
    (plannedRates, billingAccounts: BillingAcount[]) => {
        return plannedRates
            .filter((rate) => SETTINGS_PAGE_TRIAL_RATES.has(rate.rate))
            .map((rate) => {
                const isWebTrial = SETTINGS_PAGE_WEB_TRIAL_RATES.has(rate.rate);

                const bonusAccount = billingAccounts.reduce(
                    (result, { rate: rateName, type, units }) => {
                        const isBonusType =
                            type === AccountType.CLIENT_BONUS ||
                            type === AccountType.CLIENT_BONUS_CUSTOM;
                        const isIntegrationRateType =
                            rateName === AccountRate.INTEGRATION_TRIAL ||
                            rateName === AccountRate.INTEGRATION;

                        if (isBonusType && isIntegrationRateType) {
                            result += units;
                        }

                        return result;
                    },
                    0,
                );

                return {
                    type: rate.rate,
                    name:
                        ACCOUNT_RATE_TITLE_MAP[rate.rate as AccountRate.START],
                    startDate: moment(rate.startDate).format('DD.MM.YYYY'),
                    isWebTrial,
                    employeesCount: `0/100`,
                    bonusAccount: bonusAccount.toString() || '',
                };
            });
    },
);
